import type { Column } from "@tanstack/react-table";
import { isEmpty } from "common/utils/isEmpty";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/Checkbox";
import useLocalStorage from "ui/hooks/useLocalStorage";
import OverflowMenu from "ui/popovers/OverflowMenu";

export default function ShowHideColumns<T>({ columns }: { columns: Column<T>[] }) {
  const defaultVisibility = useRef(Object.fromEntries(columns.map((column) => [column.id, column.getIsVisible()])));

  // Save column visibility in localStorage - uses a key concatenated from column ids
  const [columnVisibility, setColumnVisibility] = useLocalStorage<Record<string, boolean>>(
    columns.map((column) => column.id).join("-"),
    {},
  );

  useEffect(() => {
    if (!isEmpty(columnVisibility)) {
      columns.forEach((column) => {
        if (typeof columnVisibility[column.id] === "boolean") {
          // Wait a render cycle to ensure the column is rendered
          requestAnimationFrame(() => column.toggleVisibility(columnVisibility[column.id]));
        }
      });
    }
  }, [columnVisibility]);

  return (
    <OverflowMenu
      title="Column Visibility"
      showCloseIcon
      menuItems={columns.map((column) => ({
        onClick: () => {
          const isVisible = column.getIsVisible();
          column.toggleVisibility();
          setColumnVisibility({ ...columnVisibility, [column.id]: !isVisible });
        },
        children: (
          <Checkbox
            key={column.id}
            checked={column.getIsVisible()}
            label={column.columnDef.header as ReactNode}
            data-testid={`column-visibility-checkbox-${column.id}`}
          />
        ),
      }))}
    >
      <Button
        btnStyle="secondary"
        btnSize="medium"
        onClick={() => {
          columns.forEach((column) => column.toggleVisibility(defaultVisibility.current[column.id]));
          setColumnVisibility({});
        }}
        dataTestId="reset-columns-button"
      >
        Reset to Default
      </Button>
    </OverflowMenu>
  );
}
